import type { VariantProps } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import { useStickToBottomContext } from "use-stick-to-bottom";
import { Button, type buttonVariants } from "@/components/ui/atoms/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/atoms/tooltip";
import { cn } from "@/lib/utils";

type ScrollButtonProps = {
  variant?: VariantProps<typeof buttonVariants>["variant"];
  size?: VariantProps<typeof buttonVariants>["size"];
} & React.ComponentProps<"button">;

function ScrollButton({
  className,
  variant = "outline",
  size = "sm",
  ...props
}: ScrollButtonProps) {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-hidden={isAtBottom}
            aria-label="Scroll to bottom"
            className={cn(
              "h-10 w-10 rounded-full transition-all duration-150 ease-out",
              isAtBottom
                ? "pointer-events-none translate-y-4 scale-95 opacity-0"
                : "translate-y-0 scale-100 opacity-100",
              className
            )}
            data-slot="scroll-button"
            onClick={() => scrollToBottom()}
            size={size}
            tabIndex={isAtBottom ? -1 : 0}
            variant={variant}
            {...props}
          />
        }
      >
        <ChevronDown className="h-5 w-5" />
      </TooltipTrigger>
      {!isAtBottom && <TooltipContent>Scroll to bottom</TooltipContent>}
    </Tooltip>
  );
}

export type { ScrollButtonProps };
export { ScrollButton };
